import { Card } from "@insights/ui";
import {
  BookOpen,
  Cable,
  ChartNoAxesCombined,
  ChevronRight,
  Settings,
  Waypoints,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router";
import { PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import "../styles/documentation.css";

export type DocumentationChapter =
  | "overview"
  | "connect"
  | "builds"
  | "pulse"
  | "workspace";

type ChapterContent = {
  eyebrow: string;
  title: string;
  description: string;
  icon: LucideIcon;
  sections: Array<{
    title: string;
    body: ReactNode;
  }>;
};

const chapterOrder: DocumentationChapter[] = [
  "overview",
  "connect",
  "builds",
  "pulse",
  "workspace",
];

function workspacePath(workspaceId: string | undefined, path: string) {
  return `/w/${workspaceId ?? ""}/${path}`;
}

function Callout({ children }: { children: ReactNode }) {
  return <aside className="documentation-callout">{children}</aside>;
}

function chapterContent(
  workspaceId: string | undefined,
): Record<DocumentationChapter, ChapterContent> {
  return {
    overview: {
      eyebrow: "Documentation · Chapter 1",
      title: "Start with Insights",
      description:
        "Insights brings build activity, pull-request signals, releases, and cache usage into one workspace.",
      icon: BookOpen,
      sections: [
        {
          title: "What belongs in a workspace",
          body: (
            <>
              <p>
                A workspace is the boundary for repositories, build data,
                members, teams, credentials, and billing. Use one workspace for
                people and repositories that should be measured together.
              </p>
              <p>
                The workspace picker in the sidebar changes this boundary. The
                filters inside Pulse narrow the current workspace; they do not
                move data between workspaces.
              </p>
            </>
          ),
        },
        {
          title: "A useful first session",
          body: (
            <ol>
              <li>
                Connect GitHub and select the repositories you want to measure.
              </li>
              <li>
                Connect Terrabuild so builds, artifacts, and cache activity
                reach the same workspace.
              </li>
              <li>
                Invite the team and map each member to the GitHub login used in
                repository activity.
              </li>
              <li>
                Let data collect before treating a short-lived change as a
                trend.
              </li>
            </ol>
          ),
        },
        {
          title: "How to read the product",
          body: (
            <>
              <p>
                Builds is the operational record: what ran, where it ran, and
                whether it passed. Pulse turns that record and GitHub activity
                into questions about flow, feedback, delivery, and behavior.
              </p>
              <Callout>
                Use a metric to find a question, then inspect its evidence. A
                number is a prompt for investigation, not a performance grade.
              </Callout>
            </>
          ),
        },
      ],
    },
    connect: {
      eyebrow: "Documentation · Chapter 2",
      title: "Connect your data",
      description:
        "GitHub supplies collaboration history. Terrabuild supplies builds, releases, artifacts, and cache activity.",
      icon: Cable,
      sections: [
        {
          title: "Connect GitHub",
          body: (
            <ol>
              <li>
                Open{" "}
                <Link to={workspacePath(workspaceId, "github")}>
                  Integrations → GitHub
                </Link>{" "}
                and install the Insights GitHub App.
              </li>
              <li>
                Select the repositories to synchronize, then save the
                selection. Saving starts a sync.
              </li>
              <li>
                Return to the page to check the latest successful sync and
                webhook timestamps. Use <strong>Sync now</strong> after changing
                access outside Insights.
              </li>
            </ol>
          ),
        },
        {
          title: "Connect Terrabuild",
          body: (
            <ol>
              <li>
                Open{" "}
                <Link
                  to={workspacePath(workspaceId, "integration/terrabuild")}
                >
                  Integrations → Terrabuild
                </Link>{" "}
                and copy the workspace block into your repository&apos;s{" "}
                <code>WORKSPACE</code> file.
              </li>
              <li>
                In{" "}
                <Link to={workspacePath(workspaceId, "security")}>
                  Settings → Security
                </Link>
                , create a contributor token. It is shown once, so copy it into
                a protected CI secret immediately.
              </li>
              <li>
                Run the generated login command from CI with the contributor
                token and master key, then trigger a build.
              </li>
            </ol>
          ),
        },
        {
          title: "Confirm the connection",
          body: (
            <>
              <p>
                A Terrabuild run should appear in{" "}
                <Link to={workspacePath(workspaceId, "builds")}>
                  Builds → Latest
                </Link>
                . Pull requests, reviews, and comments become available to
                Pulse after the GitHub sync completes.
              </p>
              <Callout>
                If GitHub data looks incomplete, check the repository selection
                before reconnecting the app. If builds are missing, verify that
                CI uses this workspace&apos;s ID and an active contributor token.
              </Callout>
            </>
          ),
        },
      ],
    },
    builds: {
      eyebrow: "Documentation · Chapter 3",
      title: "Follow delivery",
      description:
        "Use the build record to move from a recent run to its environment history and release boundary.",
      icon: ChartNoAxesCombined,
      sections: [
        {
          title: "Find a build",
          body: (
            <>
              <p>
                <Link to={workspacePath(workspaceId, "builds")}>Latest</Link>{" "}
                lists recent runs across repositories. Filter by repository,
                expand grouped runs, and open a build to inspect its steps,
                artifacts, timing, and raw payload.
              </p>
              <p>
                <Link
                  to={workspacePath(workspaceId, "builds/branch-or-tag")}
                >
                  Branch or Tag
                </Link>{" "}
                keeps the most recent run for each branch or tag in view. Use
                it when you care about current coverage rather than every run.
              </p>
            </>
          ),
        },
        {
          title: "Read the timeline",
          body: (
            <p>
              <Link to={workspacePath(workspaceId, "builds/timeline")}>
                Timeline
              </Link>{" "}
              arranges builds along an environment history. It is the quickest
              way to see what reached an environment, in what order, and where
              delivery stopped.
            </p>
          ),
        },
        {
          title: "Define releases",
          body: (
            <>
              <p>
                Owners and admins define release targets, profiles, and markers
                in{" "}
                <Link
                  to={workspacePath(workspaceId, "release-rules")}
                >
                  Settings → Release Rules
                </Link>
                . A successful build containing a configured target becomes a
                release point.
              </p>
              <p>
                <Link
                  to={workspacePath(workspaceId, "builds/comparison")}
                >
                  Comparison
                </Link>{" "}
                uses those boundaries to assemble the changes between Git states. A comparison does not imply a deployment.
                Configure release detection before relying on delivery metrics
                or generated notes.
              </p>
              <p>
                Use the copy button on Timeline or Comparison to generate release
                notes. The clipboard includes formatted HTML for rich-text apps
                and Markdown for text editors. If your browser blocks the first
                copy, click again when the notes are ready. AI failures can be
                retried; Factual remains available in Release Rules.
              </p>
            </>
          ),
        },
      ],
    },
    pulse: {
      eyebrow: "Documentation · Chapter 4",
      title: "Use Pulse",
      description:
        "Pulse groups engineering signals by the question they help answer, while keeping the underlying evidence close.",
      icon: Waypoints,
      sections: [
        {
          title: "Choose a scope",
          body: (
            <>
              <p>
                Start with Inbox, then filter by repository or team. Findings
                use a fixed rolling 90-day window so their priorities remain
                comparable.
              </p>
              <p>
                Metric pages also support period and contributor filters. Keep
                the same scope while comparing cards. A narrow scope with few
                observations can move sharply without representing a durable
                change.
              </p>
            </>
          ),
        },
        {
          title: "Pick the right question",
          body: (
            <dl>
              <div>
                <dt>Inbox</dt>
                <dd>
                  Which evidence-backed code, ownership, review, maintenance,
                  or removal questions deserve attention first?
                </dd>
              </div>
              <div>
                <dt>Activity</dt>
                <dd>
                  How much completed work, build activity, release activity,
                  and estimated build time saving appeared in each completed
                  period?
                </dd>
              </div>
              <div>
                <dt>Involvement</dt>
                <dd>
                  How many distinct people triggered builds for a repository
                  project?
                </dd>
              </div>
              <div>
                <dt>Flow</dt>
                <dd>
                  Is work moving through the system, or accumulating and
                  waiting?
                </dd>
              </div>
              <div>
                <dt>Feedback</dt>
                <dd>
                  How quickly and reliably does the system report whether a
                  change is healthy?
                </dd>
              </div>
              <div>
                <dt>Delivery</dt>
                <dd>
                  How regularly do completed changes reach an observable
                  release boundary?
                </dd>
              </div>
              <div>
                <dt>Behavior</dt>
                <dd>
                  Where do persistent differences within the selected cohort
                  merit a closer look?
                </dd>
              </div>
            </dl>
          ),
        },
        {
          title: "Move from signal to evidence",
          body: (
            <>
              <p>
                Open a metric&apos;s chart to see its weekly series. Inspect the
                evidence to find the builds or GitHub artifacts behind the
                aggregate. Observations summarize notable movement; persistent
                differences identify repeated variation inside the cohort.
              </p>
              <Callout>
                Compare like with like. Changing the repository, team, person,
                or period changes the population behind the metric.
              </Callout>
            </>
          ),
        },
      ],
    },
    workspace: {
      eyebrow: "Documentation · Chapter 5",
      title: "Run the workspace",
      description:
        "Keep membership, identity, credentials, cache policy, and billing aligned with the workspace boundary.",
      icon: Settings,
      sections: [
        {
          title: "Members and teams",
          body: (
            <p>
              In <Link to={workspacePath(workspaceId, "teams")}>Teams</Link>,
              invite members, assign roles, create team tags, and map members to
              GitHub logins. Accurate mappings let team and contributor filters
              include the right repository activity.
            </p>
          ),
        },
        {
          title: "Credentials",
          body: (
            <>
              <p>
                <Link to={workspacePath(workspaceId, "security")}>
                  Security
                </Link>{" "}
                holds personal passkeys and workspace-scoped Terrabuild tokens.
                Read-only tokens can inspect data; contributor tokens can
                publish build data.
              </p>
              <Callout>
                Give CI its own named token. Revoke that token when the job or
                environment is retired instead of sharing a personal credential.
              </Callout>
            </>
          ),
        },
        {
          title: "Release rules",
          body: (
            <p>
              In{" "}
              <Link to={workspacePath(workspaceId, "release-rules")}>
                Settings → Release Rules
              </Link>
              , owners and admins define which successful build targets count
              as releases. Profiles match environments and branches, while
              categories and badge markers control how changes are labeled in
              rendered and copied release notes. These rules apply across the
              workspace. Choose Factual, Pragmatic, or Playful as the release
              notes personality. Pragmatic is the default.
            </p>
          ),
        },
        {
          title: "Cache and billing",
          body: (
            <p>
              <Link to={workspacePath(workspaceId, "cache")}>Cache</Link> shows
              stored data, object count, monthly uploads, and recent
              consumption. Owners and admins can set a retention period.
              Billing and invoices are visible only to the workspace owner.
            </p>
          ),
        },
        {
          title: "Roles at a glance",
          body: (
            <dl>
              <div>
                <dt>Owner</dt>
                <dd>
                  Controls the subscription and invoices, and has full
                  workspace administration.
                </dd>
              </div>
              <div>
                <dt>Admin</dt>
                <dd>
                  Manages members, integrations, build configuration,
                  credentials, and cache policy.
                </dd>
              </div>
              <div>
                <dt>Member</dt>
                <dd>
                  Uses workspace data and personal settings without changing
                  administrative configuration.
                </dd>
              </div>
            </dl>
          ),
        },
      ],
    },
  };
}

export function DocumentationPage({
  chapter,
}: {
  chapter: DocumentationChapter;
}) {
  const { workspaceId } = useParams();
  const content = chapterContent(workspaceId)[chapter];
  const ChapterIcon = content.icon;
  const chapterIndex = chapterOrder.indexOf(chapter);
  const nextChapter = chapterOrder[chapterIndex + 1];

  return (
    <AppShell>
      <main className="documentation-page">
        <PageHeader
          eyebrow={content.eyebrow}
          title={content.title}
          description={content.description}
          titleAction={<ChapterIcon aria-hidden="true" />}
        />
        <article className="documentation-article">
          {content.sections.map((section, index) => {
            const headingId = `section-${index + 1}`;

            return (
              <Card
                as="section"
                aria-labelledby={headingId}
                className="documentation-section"
                key={section.title}
              >
                <span className="documentation-section-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2 id={headingId}>{section.title}</h2>
                  {section.body}
                </div>
              </Card>
            );
          })}
        </article>
        {nextChapter && (
          <Link
            className="documentation-next"
            to={workspacePath(workspaceId, `docs/${nextChapter}`)}
          >
            Continue to the next chapter
            <ChevronRight aria-hidden="true" />
          </Link>
        )}
      </main>
    </AppShell>
  );
}
